import { useState, useRef, useCallback, useEffect } from 'react';
import type { MouseEventHandler } from 'react';
import { IconAnimationTriggers } from '../Button.types';
import { TRANSITION_DURATION } from '../constants';

export function useHoverState(
  iconAnimationTriggers: Array<IconAnimationTriggers>,
  onAnimationForward: () => void,
  onAnimationBackward: () => void,
  propsOnMouseEnter?: MouseEventHandler,
  propsOnMouseLeave?: MouseEventHandler,
): { active: boolean; onMouseEnter: MouseEventHandler; onMouseLeave: MouseEventHandler } {
  const [active, setActive] = useState(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const onMouseEnter = useCallback<MouseEventHandler>(
    event => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
      setActive(true);
      if (iconAnimationTriggers.includes(IconAnimationTriggers.HOVER)) {
        onAnimationForward();
      }
      propsOnMouseEnter?.(event);
    },
    [iconAnimationTriggers, onAnimationForward, propsOnMouseEnter],
  );

  const onMouseLeave = useCallback<MouseEventHandler>(
    event => {
      animationTimeoutRef.current = setTimeout(() => {
        setActive(false);
      }, TRANSITION_DURATION);
      if (iconAnimationTriggers.includes(IconAnimationTriggers.HOVER)) {
        onAnimationBackward();
      }
      propsOnMouseLeave?.(event);
    },
    [iconAnimationTriggers, onAnimationBackward, propsOnMouseLeave],
  );

  return { active, onMouseEnter, onMouseLeave };
}
